import { css } from '@emotion/react';
import { PracticeAnalyticsTypes } from '@frontend/api-analytics';
import { DemoChip } from '@frontend/analytics-shared';
import { ChartHeaderAction } from '@frontend/charts';
import { useTranslation } from '@frontend/i18n';
import { theme } from '@frontend/theme';
import { Modal, useModalControl } from '@frontend/design-system';
import { usePracticeAnalyticsShallowStore } from './hooks/use-practice-analytics-store';
import { MonthlyTrend } from './monthly-trend';

type Props = {
  defaultKey: string;
  metric: PracticeAnalyticsTypes.Metric;
  tabs?: Record<string, string>;
  title: string;
  trackingId?: string;
};

export const useMonthlyTrendChartModal = ({
  title,
  trackingId,
  ...rest
}: Props): {
  modalRenderer: () => JSX.Element;
  triggerProps: ChartHeaderAction;
} => {
  const { t } = useTranslation('analytics');
  const { closeModal, modalProps, openModal } = useModalControl();
  const { showDemoChipAndBanner } = usePracticeAnalyticsShallowStore('showDemoChipAndBanner');

  return {
    modalRenderer: () => (
      <Modal {...modalProps} maxWidth={800}>
        <Modal.Header
          css={showDemoChipAndBanner ? demoHeaderStyles : null}
          onClose={closeModal}
          style={{ marginBottom: theme.spacing(1) }}
        >
          {showDemoChipAndBanner && <DemoChip />}
          {title}
        </Modal.Header>
        <Modal.Body>
          <MonthlyTrend {...rest} />
        </Modal.Body>
      </Modal>
    ),
    triggerProps: {
      buttonVariant: 'secondary',
      iconName: 'analytics-info',
      label: t('Monthly Trend'),
      onClick: openModal,
      trackingId,
    },
  };
};

export const demoHeaderStyles = css`
  > h2 {
    align-items: center;
    display: flex;
    gap: ${theme.spacing(0.5)};
  }
`;
